import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChartLineUp, Pulse, Eye, ArrowsClockwise } from "@phosphor-icons/react";

const POLL_MS = 8000;
const CATEGORY_COLORS = {
    news: "text-[#FFD000]",
    reviews: "text-sky-300",
    videos: "text-violet-300",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
}

function timeAgo(iso) {
    if (!iso) return "—";
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminAnalytics() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ totals: null, items: [] });
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState("");
    const [lastUpdate, setLastUpdate] = useState(null);
    const pollRef = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setBusy(true);
        try {
            const { data: payload } = await api.get("/admin/analytics");
            setData(payload);
            setLastUpdate(new Date());
            setError("");
        } catch {
            setError("Failed to load analytics");
        } finally {
            if (!silent) setBusy(false);
        }
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!user || !user.is_admin) {
            navigate("/login", { replace: true });
            return;
        }
        load();
        pollRef.current = setInterval(() => load(true), POLL_MS);
        return () => clearInterval(pollRef.current);
    }, [user, loading, navigate, load]);

    if (loading || !user) {
        return <div className="min-h-screen grid place-items-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Checking access...</div>;
    }

    const totals = data.totals || { total_articles: 0, total_views: 0, views_24h: 0, views_1h: 0, views_live: 0 };
    const items = data.items || [];

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12" data-testid="admin-analytics-page">
            <div className="border-b border-zinc-800 pb-8 mb-10 flex flex-wrap items-end justify-between gap-6">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 hover:text-[#FFD000] mb-4" data-testid="back-to-dashboard">
                        <ArrowLeft size={14} weight="bold" /> Dashboard
                    </Link>
                    <div className="overline mb-3">// Real-time</div>
                    <h1 className="font-display text-white text-5xl md:text-7xl leading-[0.9] tracking-tight">
                        ANALYTICS<span className="text-[#FFD000]">.</span>
                    </h1>
                    <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-3">
                        <span className="inline-flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            Live · updates every {POLL_MS / 1000}s
                        </span>
                        <span className="text-zinc-700">/</span>
                        <span>Last sync: {lastUpdate ? timeAgo(lastUpdate.toISOString()) : "—"}</span>
                    </div>
                </div>
                <button
                    onClick={() => load(false)}
                    disabled={busy}
                    data-testid="analytics-refresh"
                    className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                >
                    <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
                    Refresh now
                </button>
            </div>

            {error && (
                <div className="mb-8 border border-red-900 bg-red-950/40 text-red-200 px-4 py-3 text-sm font-mono uppercase tracking-[0.15em]">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-800 mb-10" data-testid="analytics-totals">
                <Stat icon={<Pulse size={16} weight="bold" />} label="Live (5m)" value={totals.views_live} accent="emerald" />
                <Stat icon={<ChartLineUp size={16} weight="bold" />} label="Past Hour" value={totals.views_1h} />
                <Stat icon={<ChartLineUp size={16} weight="bold" />} label="Past 24h" value={totals.views_24h} />
                <Stat icon={<Eye size={16} weight="bold" />} label="All-time Views" value={totals.total_views} />
                <Stat label="Articles" value={totals.total_articles} />
            </div>

            <div className="border border-zinc-800" data-testid="analytics-table">
                <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 bg-[#121212] font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <div className="col-span-5">Article</div>
                    <div className="col-span-1 text-center">Live</div>
                    <div className="col-span-1 text-center">1h</div>
                    <div className="col-span-1 text-center">24h</div>
                    <div className="col-span-1 text-center">Total</div>
                    <div className="col-span-3">Last viewed</div>
                </div>
                {items.length === 0 && !busy ? (
                    <div className="px-6 py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
                        No articles yet.
                    </div>
                ) : (
                    items.map(it => (
                        <AnalyticsRow key={it.slug} item={it} />
                    ))
                )}
            </div>
        </div>
    );
}

function Stat({ icon, label, value, accent }) {
    const numClass = accent === "emerald" && value > 0 ? "text-emerald-400" : "text-white";
    return (
        <div className="bg-[#0A0A0A] p-6">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                {icon}
                {label}
            </div>
            <div className={`font-display text-5xl mt-2 ${numClass}`}>{value}</div>
        </div>
    );
}

function AnalyticsRow({ item }) {
    const live = item.views_live;
    const liveClass = live > 0 ? "text-emerald-400 font-bold" : "text-zinc-500";
    const catClass = CATEGORY_COLORS[item.category] || "text-zinc-300";
    return (
        <div
            className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 last:border-b-0 items-center hover:bg-zinc-900/30"
            data-testid={`analytics-row-${item.slug}`}
        >
            <div className="col-span-5">
                <Link to={`/post/${item.slug}`} className="text-white hover:text-[#FFD000] font-medium block leading-tight">
                    {item.title}
                </Link>
                <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em]">
                    <span className={catClass}>{item.category}</span>
                    <span className="text-zinc-700">/</span>
                    <span className={item.published ? "text-zinc-500" : "text-orange-400"}>
                        {item.published ? "Published" : "Draft"}
                    </span>
                </div>
            </div>
            <div className={`col-span-1 text-center font-display text-2xl ${liveClass}`}>
                {live > 0 && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2 animate-pulse align-middle" />}
                {live}
            </div>
            <div className="col-span-1 text-center font-display text-2xl text-zinc-300">{item.views_1h}</div>
            <div className="col-span-1 text-center font-display text-2xl text-zinc-300">{item.views_24h}</div>
            <div className="col-span-1 text-center font-display text-2xl text-[#FFD000]">{item.views_total}</div>
            <div className="col-span-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {timeAgo(item.last_viewed_at)}
                <div className="text-zinc-700 normal-case tracking-normal text-[10px]">{fmtDate(item.last_viewed_at)}</div>
            </div>
        </div>
    );
}
